/**
 * Lodge Search Service
 *
 * Client for the "search first, browse lodges after" flow on
 * /booking/lodges (Airbnb-style): dates + guests → matching units with
 * live pricing. Hits the backend directly, no caching — availability
 * changes constantly and a stale result could show a unit as bookable
 * when it no longer is.
 *
 * @see components/booking/LodgeSearchBar.tsx
 */

import { getSessionId } from '@/lib/session';

export interface LodgeSearchParams {
  checkIn: string; // YYYY-MM-DD
  checkOut: string; // YYYY-MM-DD
  guests: number;
  locationId?: string; // scope the search to a single lodge, e.g. from /booking/lodges/{locationId}
}

export interface LodgeSearchResultItem {
  unitId: string;
  name: string;
  locationId: string;
  unitType: 'room' | 'full_lodge' | 'premium' | string;
  image?: string;
  maxOccupancy: number;
  bedrooms?: number;
  minNights?: number;
  pricing: {
    total: number;
    accommodation: number;
    cleaningFee: number;
    pricePerNight: number;
    currency: string;
    nights: number;
  };
}

export interface LodgeSearchResponse {
  startDate: string;
  endDate: string;
  guests: number;
  nights: number;
  results: LodgeSearchResultItem[];
  totalCandidates: number;
  unavailable: Array<{ unitId: string; reason: string }>;
}

interface SearchApiResponse {
  success: boolean;
  data?: LodgeSearchResponse;
  error?: { message?: string } | string;
}

/**
 * Get the API base URL (Cloud Functions in production, relative path
 * under emulators/rewrites otherwise). Same pattern used across
 * lib/hooks and components/booking.
 */
function getApiUrl(): string {
  const apiBase = process.env.NEXT_PUBLIC_API_URL;
  return apiBase ? `${apiBase}/api` : '/api';
}

/**
 * Search available lodges/units for a date range + guest count.
 *
 * @param params Check-in/check-out (YYYY-MM-DD) and guest count
 * @param signal Optional AbortSignal to cancel an in-flight search
 * @throws Error with the backend's message when the search fails.
 *   AbortError propagates untouched so callers can distinguish a
 *   user-cancelled search from a real failure.
 */
export async function searchLodges(
  params: LodgeSearchParams,
  signal?: AbortSignal
): Promise<LodgeSearchResponse> {
  const query = new URLSearchParams({
    startDate: params.checkIn,
    endDate: params.checkOut,
    guests: params.guests.toString(),
    sessionId: getSessionId(),
  });
  if (params.locationId) {
    query.set('locationId', params.locationId);
  }

  const response = await fetch(`${getApiUrl()}/availability/search?${query}`, {
    signal,
    cache: 'no-store',
  });

  const data: SearchApiResponse = await response.json();

  if (!response.ok || data.success !== true || !data.data) {
    const message =
      typeof data.error === 'string'
        ? data.error
        : data.error?.message || 'No se pudo buscar disponibilidad';
    throw new Error(message);
  }

  return data.data;
}
